import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getUserByPhone, updateUser } from '../../utils/sharedStorage';

function generateTempPassword(): string {
  const digits = '0123456789';
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  let pwd = '';
  pwd += upper.charAt(Math.floor(Math.random() * upper.length));
  pwd += lower.charAt(Math.floor(Math.random() * lower.length));
  for (let i = 0; i < 4; i++) {
    pwd += digits.charAt(Math.floor(Math.random() * digits.length));
  }
  // Mélanger
  return pwd.split('').sort(() => Math.random() - 0.5).join('');
}

type Step = 'phone' | 'newPassword';

export default function MotDePasseOublie() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const wrapClass = (field: string) =>
    `relative flex items-center border-2 rounded-2xl transition-all duration-200 bg-gray-50 ${
      focusedField === field
        ? 'border-red-500 bg-white shadow-sm shadow-red-100'
        : 'border-gray-200'
    }`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const phoneClean = phone.trim().replace(/\s+/g, '');
    if (!phoneClean) {
      setError('Veuillez entrer votre numéro de téléphone');
      return;
    }

    setIsLoading(true);

    try {
      // ✅ Chercher l'utilisateur uniquement dans Supabase
      const user = await getUserByPhone(phoneClean);

      if (!user) {
        setError('Aucun compte trouvé avec ce numéro de téléphone.');
        setIsLoading(false);
        return;
      }

      // ✅ Générer un nouveau mot de passe temporaire
      const tempPwd = generateTempPassword();

      // ✅ Mettre à jour dans Supabase uniquement
      await updateUser(phoneClean, { password: tempPwd });

      // ✅ Mettre à jour currentUser si c'est l'utilisateur connecté sur cet appareil
      const currentUserData = localStorage.getItem('currentUser');
      if (currentUserData) {
        try {
          const cu = JSON.parse(currentUserData);
          if (cu.phone === phoneClean) {
            cu.password = tempPwd;
            localStorage.setItem('currentUser', JSON.stringify(cu));
          }
        } catch { /* ignore */ }
      }

      setGeneratedPassword(tempPwd);
      setStep('newPassword');
      setIsLoading(false);
    } catch (err) {
      console.error('Erreur réinitialisation:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedPassword).catch(() => {
      const el = document.createElement('textarea');
      el.value = generatedPassword;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-red-700 via-red-600 to-orange-500 lg:flex-row flex-col">

      {/* ===== PANNEAU GAUCHE (desktop uniquement) ===== */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-red-700 via-red-600 to-orange-500 flex-col items-center justify-center p-12">
        <div className="absolute top-[-80px] left-[-80px] w-72 h-72 rounded-full bg-white/10"></div>
        <div className="absolute bottom-[-60px] right-[-60px] w-96 h-96 rounded-full bg-white/10"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-white/5"></div>

        <div className="relative z-10 text-center">
          <div className="w-28 h-28 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-2xl">
            <img
              src="https://static.readdy.ai/image/82792080bea9235a3a5c58a151cce2b4/fff4bb4e89a9e983dddf5fffb9dae068.png"
              alt="SUCCESSLINK AGENCY"
              className="w-20 h-20 object-contain"
            />
          </div>
          <h1 className="text-4xl font-extrabold text-white mb-3 tracking-tight">SUCCESSLINK</h1>
          <p className="text-red-100 text-lg font-medium mb-8">AGENCY</p>

          <div className="space-y-4 text-left">
            {[
              { icon: 'ri-lock-unlock-line', text: 'Récupérez votre accès facilement' },
              { icon: 'ri-key-2-line', text: 'Nouveau mot de passe généré instantanément' },
              { icon: 'ri-shield-check-line', text: 'Processus 100% sécurisé' },
              { icon: 'ri-customer-service-2-line', text: 'Support disponible 24h/24' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/15 backdrop-blur-sm rounded-xl px-4 py-3">
                <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg">
                  <i className={`${item.icon} text-white text-base`}></i>
                </div>
                <span className="text-white text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== PANNEAU DROIT — FORMULAIRE ===== */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-start lg:justify-center lg:bg-gray-50 bg-transparent px-6 pb-10 pt-8 lg:py-10">

        {/* Logo mobile */}
        <div className="lg:hidden flex flex-col items-center mb-6">
          <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-3 shadow-xl">
            <img
              src="https://static.readdy.ai/image/82792080bea9235a3a5c58a151cce2b4/fff4bb4e89a9e983dddf5fffb9dae068.png"
              alt="SUCCESSLINK AGENCY"
              className="w-14 h-14 object-contain"
            />
          </div>
          <h1 className="text-2xl font-extrabold text-white tracking-tight">SUCCESSLINK</h1>
          <p className="text-red-100 text-sm font-medium">AGENCY</p>
        </div>

        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">

            {/* ===== ÉTAPE 1 : Saisie du numéro ===== */}
            {step === 'phone' && (
              <>
                <div className="mb-6">
                  <div className="w-14 h-14 flex items-center justify-center bg-red-100 rounded-2xl mb-4">
                    <i className="ri-lock-unlock-line text-3xl text-red-600"></i>
                  </div>
                  <h2 className="text-2xl font-extrabold text-gray-900">Mot de passe oublié 🔑</h2>
                  <p className="text-gray-500 text-sm mt-1">
                    Entrez votre numéro de téléphone pour recevoir un nouveau mot de passe temporaire.
                  </p>
                </div>

                {error && (
                  <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-error-warning-fill text-red-500 text-lg"></i>
                    </div>
                    <p className="text-sm text-red-700 font-medium">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Numéro de téléphone
                    </label>
                    <div className={wrapClass('phone')}>
                      <div className="w-10 h-10 flex items-center justify-center ml-2 text-gray-400">
                        <i className="ri-phone-line text-lg"></i>
                      </div>
                      <input
                        type="tel"
                        required
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                        onFocus={() => setFocusedField('phone')}
                        onBlur={() => setFocusedField(null)}
                        className="flex-1 py-3.5 pr-4 bg-transparent outline-none text-sm text-gray-800 placeholder-gray-400"
                        placeholder="Ex: 97800237"
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1.5 ml-1">
                      Le numéro utilisé lors de votre inscription
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 disabled:opacity-60 text-white font-bold py-4 rounded-2xl transition-all duration-200 shadow-lg hover:shadow-red-200 hover:shadow-xl whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-base"
                  >
                    {isLoading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-xl"></i>
                        Génération en cours...
                      </>
                    ) : (
                      <>
                        <i className="ri-refresh-line text-xl"></i>
                        Générer un nouveau mot de passe
                      </>
                    )}
                  </button>
                </form>

                <div className="flex items-center gap-3 my-6">
                  <div className="flex-1 h-px bg-gray-200"></div>
                  <span className="text-xs text-gray-400 font-medium">OU</span>
                  <div className="flex-1 h-px bg-gray-200"></div>
                </div>

                <div className="text-center">
                  <Link
                    to="/connexion"
                    className="w-full flex items-center justify-center gap-2 py-3 border-2 border-red-200 text-red-600 font-bold rounded-2xl hover:bg-red-50 transition cursor-pointer text-sm"
                  >
                    <i className="ri-arrow-left-line text-base"></i>
                    Retour à la connexion
                  </Link>
                </div>
              </>
            )}

            {/* ===== ÉTAPE 2 : Affichage du nouveau mot de passe ===== */}
            {step === 'newPassword' && (
              <>
                <div className="mb-6 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                    <i className="ri-shield-check-line text-4xl text-green-600"></i>
                  </div>
                  <h2 className="text-2xl font-extrabold text-gray-900">Mot de passe généré ! ✅</h2>
                  <p className="text-gray-500 text-sm mt-1">
                    Voici votre nouveau mot de passe temporaire. Notez-le bien !
                  </p>
                </div>

                {/* Affichage du mot de passe */}
                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-300 rounded-2xl px-6 py-5 mb-4 text-center">
                  <p className="text-xs text-orange-600 font-semibold mb-2 uppercase tracking-wide">
                    Votre nouveau mot de passe
                  </p>
                  <p className="text-3xl font-extrabold text-orange-600 tracking-widest font-mono">
                    {generatedPassword}
                  </p>
                </div>

                {/* Bouton copier */}
                <button
                  onClick={handleCopy}
                  className="w-full mb-4 flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold py-3 rounded-2xl transition cursor-pointer whitespace-nowrap"
                >
                  <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                  {copied ? '✅ Copié !' : 'Copier le mot de passe'}
                </button>

                {/* Avertissement */}
                <div className="bg-yellow-50 border border-yellow-300 rounded-xl p-3 mb-5">
                  <p className="text-xs text-yellow-800 font-semibold mb-1">⚠️ Important</p>
                  <p className="text-xs text-yellow-700">
                    Notez ce mot de passe avant de fermer cette page. Vous pouvez le changer dans votre profil après connexion.
                  </p>
                </div>

                {/* Bouton se connecter */}
                <button
                  onClick={() => navigate('/connexion')}
                  className="w-full bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white font-bold py-4 rounded-2xl transition-all duration-200 shadow-lg whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-base"
                >
                  <i className="ri-login-circle-line text-xl"></i>
                  Se connecter maintenant
                </button>

                <button
                  onClick={() => { setStep('phone'); setPhone(''); setError(''); }}
                  className="w-full mt-3 py-3 text-sm text-gray-500 hover:text-red-600 font-medium cursor-pointer transition"
                >
                  ← Recommencer avec un autre numéro
                </button>
              </>
            )}
          </div>

          <p className="text-center text-xs text-white/80 mt-5 mb-4">
            <i className="ri-shield-check-line mr-1"></i>
            Récupération sécurisée — Vos données sont protégées
          </p>
        </div>
      </div>
    </div>
  );
}